import type {
  UserInvitationResponseDto,
  UserResponseDto,
  UsersControllerListUsersParams,
  UsersSummaryResponseDto,
} from '@trellis/api'
import type {
  InviteUser,
  OnboardingCardId,
  RequestResult,
  UpdateUserRole,
  UserRole,
} from '@trellis/shared'

import { env } from '@/env'
import { apiClient } from '@/lib/api-client'
import { request, requestPaged } from '@/lib/request'
import { getStoredTenantSlug } from '@/lib/tenant'

/**
 * User as returned by the non-migrated hand-rolled endpoints below
 * (role/deactivate/reactivate).
 */
export interface UserResponse {
  id: string
  name: string
  email: string
  emailVerified: boolean
  role: UserRole
  isActive: boolean
  imageUrl: string | null
  createdAt: string
}

export type UsersListParams = UsersControllerListUsersParams

export const listUsers = (params: UsersListParams = {}) =>
  requestPaged<UserResponseDto>(() => apiClient.api.usersControllerListUsers(params))

export const getPendingInvitations = () =>
  request<UserInvitationResponseDto[]>(() =>
    apiClient.api.usersControllerListPendingInvitations(),
  )

export const getUsersSummary = () =>
  request<UsersSummaryResponseDto>(() => apiClient.api.usersControllerGetUsersSummary())

export const resetUserPassword = (userId: string) =>
  request<void>(() => apiClient.api.usersControllerResetPassword({ id: userId }))

async function fetchUsers<T>(
  path: string,
  options: RequestInit = {},
): Promise<RequestResult<T>> {
  try {
    const response = await fetch(`${env.VITE_API_BASE_URL}/api${path}`, {
      ...options,
      credentials: 'include',
      headers: (() => {
        /*
         * Fastify's default JSON body parser rejects an empty body when Content-Type is
         * application/json (400 FST_ERR_CTP_EMPTY_JSON_BODY) — only set it when a body
         * is actually being sent.
         */
        const requestHeaders = new Headers(
          options.body ? { 'Content-Type': 'application/json' } : {},
        )
        if (options.headers) {
          new Headers(options.headers).forEach((value, key) =>
            requestHeaders.set(key, value),
          )
        }
        const tenantSlug = getStoredTenantSlug()
        if (tenantSlug) requestHeaders.set('X-Tenant-Slug', tenantSlug)
        return requestHeaders
      })(),
    })
    // 204 No Content — no body to parse
    if (response.status === 204) {
      return { status: 204, data: undefined as T, pagination: null, error: null }
    }
    const body = await response.json()
    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: body.code ?? 'API_ERROR',
          message: body.message ?? 'Request failed',
        },
      }
    }
    return {
      status: response.status,
      data: body.data as T,
      pagination: null,
      error: null,
    }
  } catch (caughtError) {
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message: caughtError instanceof Error ? caughtError.message : 'Request failed',
      },
    }
  }
}

export const inviteUser = (
  data: InviteUser,
): Promise<RequestResult<UserInvitationResponseDto>> =>
  fetchUsers<UserInvitationResponseDto>('/users/invitations', {
    method: 'POST',
    body: JSON.stringify(data),
  })

export const cancelInvitation = (id: string): Promise<RequestResult<null>> =>
  fetchUsers<null>(`/users/invitations/${id}`, { method: 'DELETE' })

export const updateUserRole = (
  userId: string,
  data: UpdateUserRole,
): Promise<RequestResult<UserResponse>> =>
  fetchUsers<UserResponse>(`/users/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })

export const deactivateUser = (userId: string): Promise<RequestResult<UserResponse>> =>
  fetchUsers<UserResponse>(`/users/${userId}/deactivate`, { method: 'PATCH' })

export const reactivateUser = (userId: string): Promise<RequestResult<UserResponse>> =>
  fetchUsers<UserResponse>(`/users/${userId}/reactivate`, { method: 'PATCH' })

export interface UserAvatarResponse {
  imageUrl: string
}

export const uploadUserAvatar = async (
  file: File,
): Promise<RequestResult<UserAvatarResponse>> => {
  const formData = new FormData()
  formData.append('file', file)

  try {
    const tenantSlug = getStoredTenantSlug()
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/users/me/avatar`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : undefined,
    })
    const body = await response.json()
    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: { code: body.code, message: body.message },
      }
    }
    return { status: response.status, data: body.data, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error ? caughtError.message : 'Avatar upload failed',
      },
    }
  }
}

export const deleteUserAvatar = (): Promise<RequestResult<null>> =>
  fetchUsers<null>('/users/me/avatar', { method: 'DELETE' })

export interface OnboardingStatusResponse {
  dismissedCards: OnboardingCardId[]
}

export const getOnboardingStatus = (): Promise<RequestResult<OnboardingStatusResponse>> =>
  fetchUsers<OnboardingStatusResponse>('/users/me/onboarding')

export const dismissOnboardingCard = (
  cardId: OnboardingCardId,
): Promise<RequestResult<null>> =>
  fetchUsers<null>('/users/me/onboarding', {
    method: 'PATCH',
    body: JSON.stringify({ cardId }),
  })

export interface TwoFactorNudgePreferenceResponse {
  optedOut: boolean
}

export const getTwoFactorNudgePreference = (): Promise<
  RequestResult<TwoFactorNudgePreferenceResponse>
> => fetchUsers<TwoFactorNudgePreferenceResponse>('/users/me/two-factor-nudge-preference')

export const updateTwoFactorNudgePreference = (
  optedOut: boolean,
): Promise<RequestResult<null>> =>
  fetchUsers<null>('/users/me/two-factor-nudge-preference', {
    method: 'PATCH',
    body: JSON.stringify({ optedOut }),
  })
